import React, { useMemo, useState } from 'react';
import { AlertTriangle, Clock, List, Table2 } from 'lucide-react';
import type { Profile, Tarefa } from '../../types';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';
import {
  ordenarTarefas, situacaoPrazo, rotuloPrazo, type SituacaoPrazo,
} from '../../src/lib/projetos/tarefasProjeto';

type Modo = 'tabela' | 'lista';
export type FiltroTarefas = 'todas' | 'abertas' | 'atrasadas' | 'concluidas';

const FILTROS: [FiltroTarefas, string][] = [
  ['todas', 'Todas'], ['abertas', 'Em aberto'], ['atrasadas', 'Atrasadas'], ['concluidas', 'Concluídas'],
];

const DESCARTADOS = new Set(['rejeitado', 'cancelado']);

const CHAVE_MODO = 'projetos:tarefas:modo';

const STATUS_ROTULO: Record<string, string> = {
  novo: 'Novo',
  em_analise: 'Em análise',
  em_desenvolvimento: 'Em desenvolvimento',
  em_teste: 'Em teste',
  concluido: 'Concluído',
  rejeitado: 'Rejeitado',
  cancelado: 'Cancelado',
};

const STATUS_COR: Record<string, string> = {
  concluido: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  em_desenvolvimento: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  em_teste: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  em_analise: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  rejeitado: 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 line-through',
  cancelado: 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 line-through',
};

// Atrasada em vermelho, hoje/próxima em âmbar — as mesmas cores do KPI "Atrasadas" da Visão Geral.
const DESTAQUE: Partial<Record<SituacaoPrazo, { linha: string; borda: string; selo: string }>> = {
  atrasada: {
    linha: 'bg-rose-50/70 dark:bg-rose-900/15',
    borda: 'border-l-rose-500',
    selo: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  },
  vence_hoje: {
    linha: 'bg-amber-50/70 dark:bg-amber-900/15',
    borda: 'border-l-amber-500',
    selo: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  },
  proxima: {
    linha: '',
    borda: 'border-l-amber-400',
    selo: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
};

const situacaoDe = (t: Tarefa) => t.solicitacao_status || t.status || 'novo';

const dataBR = (v?: string | null) =>
  v ? new Date(`${v.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : '—';

const lerModo = (): Modo => {
  try {
    return localStorage.getItem(CHAVE_MODO) === 'lista' ? 'lista' : 'tabela';
  } catch {
    return 'tabela';
  }
};

const SeloStatus: React.FC<{ situacao: string }> = ({ situacao }) => (
  <span className={`inline-block whitespace-nowrap text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
    STATUS_COR[situacao] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
  }`}>
    {STATUS_ROTULO[situacao] || situacao}
  </span>
);

const SeloPrazo: React.FC<{ tarefa: Tarefa; hoje: Date }> = ({ tarefa, hoje }) => {
  const s = situacaoPrazo(tarefa, hoje);
  const rotulo = rotuloPrazo(tarefa, hoje);
  const d = DESTAQUE[s];
  if (!rotulo || !d) return null;
  const Icone = s === 'atrasada' ? AlertTriangle : Clock;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-bold px-1.5 py-0.5 rounded ${d.selo}`}>
      <Icone size={10} /> {rotulo}
    </span>
  );
};

interface Props {
  tarefas: Tarefa[];
  users: Profile[];
  /** Controlado pelo pai: os cards da Visão Geral abrem esta aba já filtrada. */
  filtro: FiltroTarefas;
  onFiltro: (f: FiltroTarefas) => void;
}

const ProjetoTarefasPanel: React.FC<Props> = ({ tarefas, users, filtro, onFiltro }) => {
  const [modo, setModo] = useState<Modo>(lerModo);
  const hoje = useMemo(() => new Date(), []);

  const mudarModo = (m: Modo) => {
    setModo(m);
    try { localStorage.setItem(CHAVE_MODO, m); } catch { /* sem storage: fica só na sessão */ }
  };

  const todasMaes = useMemo(() => ordenarTarefas(tarefas.filter(t => t.parent_id == null)), [tarefas]);
  const filhasDe = useMemo(() => {
    const mapa = new Map<string, Tarefa[]>();
    for (const t of tarefas) {
      if (t.parent_id == null) continue;
      const k = String(t.parent_id);
      mapa.set(k, [...(mapa.get(k) || []), t]);
    }
    for (const [k, v] of mapa) mapa.set(k, ordenarTarefas(v));
    return mapa;
  }, [tarefas]);

  // Rejeitada/cancelada não é trabalho: sai do contador da aba (que bate com o progresso) e vai
  // para um bloco próprio no fim, em vez de ficar no meio da numeração.
  const descartadas = useMemo(() => todasMaes.filter(t => DESCARTADOS.has(situacaoDe(t))), [todasMaes]);
  const maes = useMemo(() => {
    const ativas = todasMaes.filter(t => !DESCARTADOS.has(situacaoDe(t)));
    if (filtro === 'todas') return ativas;
    return ativas.filter(t => {
      const filhas = filhasDe.get(String(t.id)) || [];
      if (filtro === 'concluidas') return situacaoDe(t) === 'concluido';
      if (filtro === 'abertas') return situacaoDe(t) !== 'concluido';
      // Mãe entra quando ela ou alguma subtarefa está atrasada — o KPI conta subtarefas também.
      return [t, ...filhas].some(x => situacaoPrazo(x, hoje) === 'atrasada');
    });
  }, [todasMaes, filhasDe, filtro, hoje]);

  const nomeDoUsuario = (uid?: string | null) => users.find(u => u.id === uid)?.full_name || '—';

  const contagem = useMemo(() => {
    let atrasadas = 0, proximas = 0;
    for (const t of tarefas) {
      const s = situacaoPrazo(t, hoje);
      if (s === 'atrasada') atrasadas++;
      else if (s === 'vence_hoje' || s === 'proxima') proximas++;
    }
    return { atrasadas, proximas };
  }, [tarefas, hoje]);

  if (todasMaes.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
        Nenhuma solicitação vinculada ainda. Vincule pela Central de Solicitações, no campo "Projeto".
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="inline-flex flex-wrap gap-1 mr-1" role="group" aria-label="Filtrar tarefas">
            {FILTROS.map(([valor, rotulo]) => (
              <button key={valor} type="button" onClick={() => onFiltro(valor)} aria-pressed={filtro === valor}
                className={`px-2.5 py-1 rounded-full font-semibold border transition-colors ${
                  filtro === valor
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}>
                {rotulo}
              </button>
            ))}
          </div>
          {contagem.atrasadas > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
              <AlertTriangle size={12} /> {contagem.atrasadas} {contagem.atrasadas === 1 ? 'atrasada' : 'atrasadas'}
            </span>
          )}
          {contagem.proximas > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              <Clock size={12} /> {contagem.proximas} vencendo em até 3 dias
            </span>
          )}
          {contagem.atrasadas === 0 && contagem.proximas === 0 && (
            <span className="text-gray-500 dark:text-gray-400">Nenhuma tarefa atrasada ou perto do vencimento.</span>
          )}
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-800" role="group" aria-label="Modo de visualização">
          {([['tabela', 'Tabela', Table2], ['lista', 'Lista', List]] as const).map(([valor, rotulo, Icone]) => (
            <button key={valor} type="button" onClick={() => mudarModo(valor)} aria-pressed={modo === valor}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                modo === valor ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
              <Icone size={13} /> {rotulo}
            </button>
          ))}
        </div>
      </div>

      {maes.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
          Nenhuma tarefa neste filtro.
        </p>
      ) : modo === 'tabela' ? (
        <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 font-semibold">Tarefa</th>
                <th className="px-3 py-2 font-semibold">Responsável</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Vencimento</th>
                <th className="px-3 py-2 font-semibold text-center">Subtarefas</th>
              </tr>
            </thead>
            <tbody>
              {maes.map(t => {
                const filhas = filhasDe.get(String(t.id)) || [];
                const concluidas = filhas.filter(f => situacaoDe(f) === 'concluido').length;
                const filhasAtrasadas = filhas.filter(f => situacaoPrazo(f, hoje) === 'atrasada').length;
                const d = DESTAQUE[situacaoPrazo(t, hoje)];
                const encerrada = situacaoPrazo(t, hoje) === 'encerrada';
                return (
                  <tr key={t.id} className={`border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${d?.linha || ''}`}>
                    <td className={`px-3 py-2 border-l-4 ${d?.borda || 'border-l-transparent'}`}>
                      <button type="button" onClick={() => abrirSolicitacao(t.id)}
                        className={`font-semibold text-left hover:text-primary ${encerrada ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-white'}`}>
                        {t.titulo}
                      </button>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">{nomeDoUsuario(t.responsavel_id)}</td>
                    <td className="px-3 py-2"><SeloStatus situacao={situacaoDe(t)} /></td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="whitespace-nowrap text-gray-600 dark:text-gray-300">{dataBR(t.vencimento)}</span>
                        <SeloPrazo tarefa={t} hoje={hoje} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap text-gray-500 dark:text-gray-400">
                      {filhas.length === 0 ? '—' : `${concluidas}/${filhas.length}`}
                      {filhasAtrasadas > 0 && (
                        <span className="ml-1 text-rose-600 dark:text-rose-400 font-semibold" title="Subtarefas atrasadas">
                          · {filhasAtrasadas} atras.
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-2">
          {maes.map(t => {
            const filhas = filhasDe.get(String(t.id)) || [];
            const d = DESTAQUE[situacaoPrazo(t, hoje)];
            return (
              <div key={t.id}
                className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-4 ${d?.borda || 'border-l-gray-200 dark:border-l-gray-700'} rounded-xl p-4 ${d?.linha || ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <button type="button" onClick={() => abrirSolicitacao(t.id)} className="font-semibold text-gray-800 dark:text-white hover:text-primary text-left">
                    {t.titulo}
                  </button>
                  <SeloStatus situacao={situacaoDe(t)} />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>
                    {nomeDoUsuario(t.responsavel_id)}
                    {t.vencimento && ` · vence ${dataBR(t.vencimento)}`}
                  </span>
                  <SeloPrazo tarefa={t} hoje={hoje} />
                </div>
                {filhas.length > 0 && (
                  <ul className="mt-2 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-1">
                    {filhas.map(f => (
                      <li key={f.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-gray-600 dark:text-gray-300">{f.titulo}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <SeloPrazo tarefa={f} hoje={hoje} />
                          <span className="text-gray-400">{STATUS_ROTULO[situacaoDe(f)] || situacaoDe(f)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filtro === 'todas' && descartadas.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
            Rejeitadas ou canceladas ({descartadas.length}) — não contam no progresso
          </p>
          <ul className="space-y-1">
            {descartadas.map(t => (
              <li key={t.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-sm">
                <button type="button" onClick={() => abrirSolicitacao(t.id)} className="text-left text-gray-500 dark:text-gray-400 hover:text-primary">
                  {t.titulo}
                </button>
                <SeloStatus situacao={situacaoDe(t)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProjetoTarefasPanel;
